import { CreateInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback, useMemo } from 'react';
import { useDataProvider, useNotify, useSaveContext } from 'react-admin';
import { FieldValues, UseFormReturn } from 'react-hook-form';

import { CreateInDialogButton, StepperForm, StepperFormStep } from '@sh4res/bo-common/components';
import { ArbitrageFormData } from '@sh4res/bo-common/components/B2bArbitrage/buildArbitrageBody';
import { arbitrageSteps } from '@sh4res/bo-common/components/B2bArbitrage/steps';
import { useArbitrageCreate } from '@sh4res/bo-common/components/B2bArbitrage/useArbitrageCreate';
import { voluntaryPaymentSteps } from '@sh4res/bo-common/components/B2bVoluntaryPayment/steps';
import { GeneralInfoStep } from '@sh4res/bo-common/components/B2bVoluntaryPayment/steps/GeneralInfoStep';
import { useVoluntaryPaymentCreate } from '@sh4res/bo-common/components/B2bVoluntaryPayment/useVoluntaryPaymentCreate';
import { validateForm } from '@sh4res/bo-common/components/StepperFormButtons';
import { useResourceBasePath } from '@sh4res/bo-common/hooks';

const isArbitrage = (values: FieldValues): values is ArbitrageFormData =>
  values.type === 'arbitrage';
const isVoluntaryPayment = (values: FieldValues) => !isArbitrage(values);

const hiddenWhen = (
  steps: StepperFormStep[],
  hidden: (values: FieldValues) => boolean,
): StepperFormStep[] => steps.map((step) => ({ ...step, hidden }));

const IndividualOperationForm = ({ steps }: { steps: StepperFormStep[] }) => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const resourceBasePath = useResourceBasePath();
  const { save } = useSaveContext();
  const saveArbitrage = useArbitrageCreate();

  const handleSave = useCallback(
    async (form: UseFormReturn<FieldValues>) => {
      if (!(await validateForm(form, notify, dataProvider, resourceBasePath))) {
        return;
      }
      await form.handleSubmit(async (values) => {
        if (isArbitrage(values)) {
          await saveArbitrage(values);
        } else {
          await save?.(values);
        }
      })();
    },
    [notify, dataProvider, resourceBasePath, save, saveArbitrage],
  );

  return (
    <StepperForm
      steps={steps}
      defaultValues={{ type: 'voluntary_payment' }}
      onSave={handleSave}
      testId="BoExternalB2bIndividualOperationCreate"
      permissions={{
        resource: 'b2b/individual_operations',
        action: 'create',
      }}
    />
  );
};

export function B2bIndividualOperationCreate(props: CreateInDialogButtonProps['ButtonProps']) {
  const resourceBasePath = useResourceBasePath();
  const { transform, mutationOptions } = useVoluntaryPaymentCreate();

  const steps: StepperFormStep[] = useMemo(
    () => [
      {
        label: 'resources.bo_common.b2b.voluntary_payment_create.steps.1',
        content: <GeneralInfoStep />,
      },
      ...hiddenWhen(voluntaryPaymentSteps, isArbitrage),
      ...hiddenWhen(arbitrageSteps, isVoluntaryPayment),
    ],
    [],
  );

  return (
    <CreateInDialogButton
      resource={`${resourceBasePath}individual_operations`}
      testId="BoExternalB2BVoluntaryPaymentCreate_CreateButton"
      fullWidth
      maxWidth="xl"
      title={'resources.bo_common.b2b.voluntary_payment_create.title'}
      ButtonProps={{
        label: 'resources.bo_common.b2b.voluntary_payment_create.button',
        ...props,
      }}
      transform={transform}
      mutationOptions={mutationOptions}>
      <IndividualOperationForm steps={steps} />
    </CreateInDialogButton>
  );
}
